import type {
    ChatStatusItem,
    PageChangeContentItem,
    PageChangeOperation,
    PageChangeSetStatusItem,
} from '../chat-status';
import {isPageChangeSetStatusItem} from '../chat-status';

type Options = {
    labels: Record<string, string>;
    currentPageId: () => string;
    /** Whether the preview is drawn now. It belongs to the Sidebar, so it shows only while the Sidebar is open. */
    isShown: () => boolean;
    /** Whether the page's buttons must wait, because a reply or another button press is still running. */
    isBlocked: () => boolean;
    /** Takes a change out of the set, the same way Skip in the chat does. */
    skip: (set: PageChangeSetStatusItem, operationId: string) => void;
};

/** Marks every element the preview adds, so it can find and remove them again. */
const PREVIEW_ATTRIBUTE = 'data-ai-preview';
const EDITED_CLASS = 'howspace-ai-preview-edited';
const SHOWING_ORIGINAL_CLASS = 'is-showing-original';
const ORIGINAL_CLASS = 'howspace-ai-preview-original';
const HIGHLIGHT_CLASS = 'is-ai-preview-highlighted';
const DESCRIPTION_PREVIEW_LENGTH = 220;

/**
 * Shows the page the way it will look once the staged changes are applied. Only the browser of the user who staged
 * them shows it, the page itself is not changed.
 *
 * The preview is put together from the real widgets on the page and placeholders, never by rendering a widget
 * from settings that are not saved. An edit shows its new values in place of the widget's own, with the original
 * a click away. An addition is a placeholder drawn where applying the set will put it.
 *
 * The page keeps changing under the preview: live updates replace widgets and containers, and page loads and mode
 * switches replace the whole page. So the preview is laid over the page again whenever the page changes, and every
 * step of that is safe to repeat. A widget someone else updates gets its preview back on top, and one someone
 * deletes takes its preview with it.
 *
 * Everything the preview inserts goes through `place()`, so moving real widgets can be added there later without
 * changing how the rest is drawn.
 */
export class PagePreview {
    private set: PageChangeSetStatusItem | null = null;
    private signature = '';
    private observer: MutationObserver | null = null;
    private frame = 0;
    /** Edits showing the widget's original values instead of the new ones. */
    private readonly showingOriginal = new Set<string>();
    /** Additions showing all of their content. */
    private readonly expanded = new Set<string>();
    /**
     * A change skipped on the page, and the change next to it. The pressed Skip goes with its change, so focus moves
     * on to the next change once it is gone.
     */
    private refocusAfterSkip: {skipped: string; next: string | null} | null = null;

    constructor(private readonly options: Options) {
    }

    render(statusItems: ChatStatusItem[]): void {
        const pageId = this.options.currentPageId();
        this.set = this.options.isShown()
            ? statusItems
                .filter(isPageChangeSetStatusItem)
                .find(set => set.status === 'staged' && set.pageId === pageId) ?? null
            : null;

        const signature = JSON.stringify([pageId, this.set?.id, this.set?.revision, this.set?.operations]);
        if (signature !== this.signature) {
            this.signature = signature;
            this.withoutObserving(() => this.clear());
        }
        this.sync();
    }

    /**
     * Scrolls to a change on the page and moves focus to it. A change that is still staged is found in the preview,
     * or for an edit of a widget open in its editor, the widget itself. An applied one is the widget it added or
     * changed. Returns false when the page does not show it, for example in a hidden or collapsed part of it.
     */
    reveal(set: PageChangeSetStatusItem, operation: PageChangeOperation): boolean {
        const page = this.pageElement();
        if (set.pageId !== this.options.currentPageId() || page === null) {
            return false;
        }

        const widgetId = set.status === 'staged' ? operation.target : operation.widgetId;
        const element = (set.status === 'staged' ? this.previewElementOf(operation.id) : null)
            ?? (widgetId === null ? null : page.querySelector<HTMLElement>(`#widget-${CSS.escape(widgetId)}`));
        if (!(element instanceof HTMLElement) || !page.contains(element) || element.getClientRects().length === 0) {
            return false;
        }

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        element.scrollIntoView({behavior: reducedMotion ? 'auto' : 'smooth', block: 'center'});
        const focusTarget = element.querySelector<HTMLElement>(`[${PREVIEW_ATTRIBUTE}="bar"]`) ?? element;
        if (!focusTarget.hasAttribute('tabindex')) {
            focusTarget.tabIndex = -1;
            focusTarget.addEventListener('blur', () => focusTarget.removeAttribute('tabindex'), {once: true});
        }
        focusTarget.focus({preventScroll: true});
        element.classList.add(HIGHLIGHT_CLASS);
        window.setTimeout(() => element.classList.remove(HIGHLIGHT_CLASS), 1600);

        return true;
    }

    /**
     * Running replies and button presses come and go without the preview changing, so only the buttons follow them.
     */
    syncDisabled(): void {
        const disabled = this.options.isBlocked();
        document.querySelectorAll<HTMLButtonElement>(`[${PREVIEW_ATTRIBUTE}] button[data-preview-skip]`)
            .forEach(button => {
                button.disabled = disabled;
            });
        this.refocusAfterSkipped();
    }

    /**
     * The Skip pressed on the page goes with its change, which drops focus to the page. Once the request is through
     * and the change is gone, focus moves on to the next change on the page instead.
     */
    private refocusAfterSkipped(): void {
        const refocus = this.refocusAfterSkip;
        if (refocus === null || this.options.isBlocked() || this.skipButtonOf(refocus.skipped) !== null) {
            return;
        }
        this.refocusAfterSkip = null;
        if (refocus.next !== null && (document.activeElement === null || document.activeElement === document.body)) {
            this.skipButtonOf(refocus.next)?.focus({preventScroll: true});
        }
    }

    private skipButtonOf(operationId: string): HTMLButtonElement | null {
        return document.querySelector<HTMLButtonElement>(`[data-preview-skip="${CSS.escape(operationId)}"]`);
    }

    /**
     * The change drawn after this one on the page, or before it when it is the last.
     */
    private neighbourOf(operationId: string): string | null {
        const skips = Array.from(document.querySelectorAll<HTMLButtonElement>('button[data-preview-skip]'));
        const index = skips.findIndex(button => button.dataset.previewSkip === operationId);
        const neighbour = index === -1 ? null : skips[index + 1] ?? skips[index - 1] ?? null;

        return neighbour?.dataset.previewSkip ?? null;
    }

    destroy(): void {
        this.set = null;
        this.stopObserving();
        this.clear();
    }

    private sync(): void {
        this.withoutObserving(() => {
            const page = this.pageElement();
            if (this.set === null || page === null) {
                this.clear();
                return;
            }

            const pending = this.set.operations.filter(operation => operation.status === 'pending');
            this.syncEdits(page, this.set, pending.filter(operation => operation.op === 'update_item'));
            this.syncAdditions(page, this.set, pending.filter(operation => operation.op === 'add_widget'));
            this.syncDisabled();
        });

        if (this.set === null) {
            this.stopObserving();
        } else {
            this.observe();
        }
    }

    private syncEdits(page: HTMLElement, set: PageChangeSetStatusItem, edits: PageChangeOperation[]): void {
        const current = new Set<HTMLElement>();
        edits.forEach(edit => {
            if (edit.target === null) {
                return;
            }
            // A widget open for editing shows its own form, never the preview.
            const selector = `.widget[data-widget-id="${CSS.escape(edit.target)}"][data-widget-mode="view"]`;
            page.querySelectorAll<HTMLElement>(selector)
                .forEach(widget => {
                    // The copy a live update is about to replace keeps its preview until it is gone.
                    if (widget.dataset.aiPreviewOp !== edit.id) {
                        this.undecorate(widget);
                        this.decorateEdit(widget, set, edit);
                    }
                    widget.classList.toggle(SHOWING_ORIGINAL_CLASS, this.showingOriginal.has(edit.id));
                    this.syncToggle(widget, edit.id);
                    current.add(widget);
                });
        });

        page.querySelectorAll<HTMLElement>(`.${EDITED_CLASS}`).forEach(widget => {
            if (!current.has(widget)) {
                this.undecorate(widget);
            }
        });
    }

    private decorateEdit(widget: HTMLElement, set: PageChangeSetStatusItem, edit: PageChangeOperation): void {
        widget.classList.add(EDITED_CLASS);
        widget.dataset.aiPreviewOp = edit.id;

        const bar = this.renderBar(set, edit, this.label('previewEdited'));
        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'howspace-ai-preview-toggle';
        toggle.dataset.previewToggle = edit.id;
        toggle.addEventListener('click', () => {
            if (this.showingOriginal.has(edit.id)) {
                this.showingOriginal.delete(edit.id);
            } else {
                this.showingOriginal.add(edit.id);
            }
            this.sync();
        });
        bar.querySelector('.howspace-ai-preview-actions')?.prepend(toggle);
        widget.prepend(bar);

        let anchor: Element = bar;
        const title = edit.fields.title;
        if (title !== undefined) {
            const heading = document.createElement('h2');
            heading.className = 'widget-title widget-padding break-word';
            heading.setAttribute(PREVIEW_ATTRIBUTE, 'value');
            const text = document.createElement('span');
            text.className = 'widget-title-text';
            text.textContent = title;
            heading.append(text);
            anchor = this.replaceInPlace(widget, '.widget-title', heading, anchor);
        }

        const body = edit.fields.text;
        if (body !== undefined) {
            const description = document.createElement('div');
            description.className = 'widget-description widget-padding break-word';
            description.setAttribute(PREVIEW_ATTRIBUTE, 'value');
            // Cleaned on the server by the same sanitizer the widget is saved through.
            description.innerHTML = body;
            this.replaceInPlace(widget, '.widget-description', description, anchor);
        }
    }

    /**
     * Puts the new value right after the original one and marks the original, so either can be shown. A widget
     * without the original element gets the new one after the previous value, or after the bar.
     */
    private replaceInPlace(widget: HTMLElement, selector: string, value: HTMLElement, fallbackAnchor: Element): Element {
        const original = Array.from(widget.querySelectorAll<HTMLElement>(selector))
            .find(element => !element.hasAttribute(PREVIEW_ATTRIBUTE));
        if (original) {
            original.classList.add(ORIGINAL_CLASS);
            original.after(value);
        } else {
            fallbackAnchor.after(value);
        }

        return value;
    }

    private syncToggle(widget: HTMLElement, operationId: string): void {
        const toggle = widget.querySelector<HTMLButtonElement>(`[data-preview-toggle="${CSS.escape(operationId)}"]`);
        if (!toggle) {
            return;
        }
        const showingOriginal = this.showingOriginal.has(operationId);
        toggle.textContent = this.label(showingOriginal ? 'previewShowChange' : 'previewShowOriginal');
        toggle.setAttribute('aria-pressed', String(showingOriginal));
    }

    private undecorate(widget: HTMLElement): void {
        widget.querySelectorAll(`[${PREVIEW_ATTRIBUTE}]`).forEach(element => element.remove());
        widget.querySelectorAll(`.${ORIGINAL_CLASS}`).forEach(element => element.classList.remove(ORIGINAL_CLASS));
        widget.classList.remove(EDITED_CLASS, SHOWING_ORIGINAL_CLASS, HIGHLIGHT_CLASS);
        delete widget.dataset.aiPreviewOp;
    }

    private syncAdditions(page: HTMLElement, set: PageChangeSetStatusItem, additions: PageChangeOperation[]): void {
        const wanted = new Set(additions.map(addition => addition.id));
        page.querySelectorAll<HTMLElement>('[data-ai-preview-placeholder]').forEach(placeholder => {
            if (!wanted.has(placeholder.dataset.aiPreviewPlaceholder || '')) {
                placeholder.remove();
            }
        });

        additions.forEach(addition => {
            const existing = this.previewElementOf(addition.id);
            if (existing === null || !page.contains(existing)) {
                existing?.remove();
                this.place(page, this.renderPlaceholder(set, addition), addition);
            }
        });

        // New rows are drawn at the end of the page in the order they will be added.
        page.querySelectorAll<HTMLElement>('.howspace-ai-preview-container').forEach(container => {
            if (container.querySelector('[data-ai-preview-placeholder]') === null) {
                container.remove();
            }
        });
    }

    /**
     * Puts an addition where applying the set will put it: into the empty container at the end of the page, or
     * into a new row drawn after the last container.
     */
    private place(page: HTMLElement, placeholder: HTMLElement, addition: PageChangeOperation): void {
        const placement = addition.placement ?? {row: Number.MAX_SAFE_INTEGER, column: 0, twoColumn: false, containerId: null};

        if (placement.containerId !== null) {
            const column = page.querySelector<HTMLElement>(`#container-${CSS.escape(placement.containerId)} .column`);
            if (column) {
                const buttons = column.querySelector(':scope > .column-buttons');
                if (buttons) {
                    buttons.before(placeholder);
                } else {
                    column.append(placeholder);
                }
                return;
            }
        }

        const columns = this.previewRow(page, placement.row, placement.twoColumn)
            .querySelectorAll<HTMLElement>('.howspace-ai-preview-column');
        (columns[Math.min(placement.column, columns.length - 1)] ?? columns[0])?.append(placeholder);
    }

    private previewRow(page: HTMLElement, row: number, twoColumn: boolean): HTMLElement {
        const existing = page.querySelector<HTMLElement>(`.howspace-ai-preview-container[data-ai-preview-row="${row}"]`);
        if (existing) {
            return existing;
        }

        const container = document.createElement('div');
        container.className = 'howspace-ai-preview-container';
        container.setAttribute(PREVIEW_ATTRIBUTE, 'row');
        container.dataset.aiPreviewRow = String(row);
        const rowElement = document.createElement('div');
        rowElement.className = `howspace-ai-preview-row${twoColumn ? ' is-two-column' : ''}`;
        for (let index = 0; index < (twoColumn ? 2 : 1); index++) {
            const column = document.createElement('div');
            column.className = 'howspace-ai-preview-column';
            rowElement.append(column);
        }
        container.append(rowElement);

        const parent = page.querySelector<HTMLElement>('.page-containers') ?? page;
        const later = Array.from(parent.querySelectorAll<HTMLElement>(':scope > .howspace-ai-preview-container'))
            .find(element => Number(element.dataset.aiPreviewRow) > row);
        if (later) {
            later.before(container);
        } else {
            parent.append(container);
        }

        return container;
    }

    private renderPlaceholder(set: PageChangeSetStatusItem, addition: PageChangeOperation): HTMLElement {
        const placeholder = document.createElement('section');
        placeholder.className = 'howspace-ai-preview-placeholder';
        placeholder.setAttribute(PREVIEW_ATTRIBUTE, 'placeholder');
        placeholder.dataset.aiPreviewPlaceholder = addition.id;
        placeholder.tabIndex = -1;
        this.fillPlaceholder(placeholder, set, addition);

        return placeholder;
    }

    private fillPlaceholder(placeholder: HTMLElement, set: PageChangeSetStatusItem, addition: PageChangeOperation): void {
        const titleId = `howspace-ai-preview-title-${set.id}-${addition.id}`;
        placeholder.setAttribute('aria-labelledby', titleId);

        const heading = document.createElement('h3');
        heading.className = 'howspace-ai-preview-title';
        const icon = document.createElement('i');
        icon.className = `fa-light fa-${addition.icon || 'square'}`;
        icon.setAttribute('aria-hidden', 'true');
        const title = document.createElement('span');
        title.id = titleId;
        title.textContent = addition.title || this.label('untitled');
        heading.append(icon, title);

        const children: HTMLElement[] = [this.renderBar(set, addition, this.label('previewNew')), heading];
        if (addition.source) {
            const source = document.createElement('p');
            source.className = 'howspace-ai-preview-source';
            source.textContent = this.label('previewSource').replace('%title%', addition.source);
            children.push(source);
        }

        const expanded = this.expanded.has(addition.id);
        const hasMore = addition.items.length > 0 || addition.description.length > DESCRIPTION_PREVIEW_LENGTH;
        if (addition.description) {
            const description = document.createElement('p');
            description.className = 'howspace-ai-preview-description';
            description.textContent = expanded || addition.description.length <= DESCRIPTION_PREVIEW_LENGTH
                ? addition.description
                : `${addition.description.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}…`;
            children.push(description);
        }

        const contentId = `howspace-ai-preview-content-${set.id}-${addition.id}`;
        if (expanded && addition.items.length > 0) {
            const items = this.renderItems(addition.items);
            items.id = contentId;
            children.push(items);
        }

        if (hasMore) {
            const toggle = document.createElement('button');
            toggle.type = 'button';
            toggle.className = 'howspace-ai-preview-expand';
            toggle.textContent = this.label(expanded ? 'previewHideContent' : 'previewShowContent');
            toggle.setAttribute('aria-expanded', String(expanded));
            if (expanded && addition.items.length > 0) {
                toggle.setAttribute('aria-controls', contentId);
            }
            toggle.addEventListener('click', () => {
                if (this.expanded.has(addition.id)) {
                    this.expanded.delete(addition.id);
                } else {
                    this.expanded.add(addition.id);
                }
                this.withoutObserving(() => {
                    placeholder.replaceChildren();
                    this.fillPlaceholder(placeholder, set, addition);
                    this.syncDisabled();
                });
                placeholder.querySelector<HTMLElement>('.howspace-ai-preview-expand')?.focus();
            });
            children.push(toggle);
        }

        placeholder.replaceChildren(...children);
    }

    private renderItems(items: PageChangeContentItem[]): HTMLElement {
        const list = document.createElement('ul');
        list.className = 'howspace-ai-preview-items';
        items.forEach(item => {
            const entry = document.createElement('li');
            const text = document.createElement('span');
            text.textContent = item.text;
            entry.append(text);
            if (item.correct) {
                entry.classList.add('is-correct');
                const mark = document.createElement('i');
                mark.className = 'fa-light fa-circle-check';
                mark.setAttribute('aria-hidden', 'true');
                const label = document.createElement('span');
                label.className = 'sr-only';
                label.textContent = this.label('previewCorrect');
                entry.append(' ', mark, label);
            }
            if (item.detail) {
                const detail = document.createElement('p');
                detail.textContent = item.detail;
                entry.append(detail);
            }
            if (item.items.length > 0) {
                entry.append(this.renderItems(item.items));
            }
            list.append(entry);
        });

        return list;
    }

    /**
     * The strip on top of every change on the page: what kind of change it is, and its buttons.
     */
    private renderBar(set: PageChangeSetStatusItem, operation: PageChangeOperation, badgeText: string): HTMLElement {
        const bar = document.createElement('div');
        bar.className = 'howspace-ai-preview-bar';
        bar.setAttribute(PREVIEW_ATTRIBUTE, 'bar');

        const badge = document.createElement('span');
        badge.className = 'howspace-ai-preview-badge';
        badge.textContent = badgeText;

        const actions = document.createElement('div');
        actions.className = 'howspace-ai-preview-actions';
        const skip = document.createElement('button');
        skip.type = 'button';
        skip.className = 'howspace-ai-preview-skip';
        skip.dataset.previewSkip = operation.id;
        skip.textContent = this.label('skip');
        skip.setAttribute(
            'aria-label',
            this.label('skipNamed').replace('%title%', operation.title || this.label('untitled')),
        );
        skip.disabled = this.options.isBlocked();
        skip.addEventListener('click', () => {
            if (!this.options.isBlocked()) {
                this.refocusAfterSkip = {skipped: operation.id, next: this.neighbourOf(operation.id)};
                this.options.skip(set, operation.id);
            }
        });
        actions.append(skip);

        bar.append(badge, actions);

        return bar;
    }

    private previewElementOf(operationId: string): HTMLElement | null {
        const escaped = CSS.escape(operationId);
        return document.querySelector<HTMLElement>(
            `[data-ai-preview-placeholder="${escaped}"], .${EDITED_CLASS}[data-ai-preview-op="${escaped}"]`,
        );
    }

    private pageElement(): HTMLElement | null {
        const pageId = this.options.currentPageId();
        return pageId === '' ? null : document.querySelector<HTMLElement>(`.page[data-page-id="${CSS.escape(pageId)}"]`);
    }

    private clear(): void {
        document.querySelectorAll<HTMLElement>(`.${EDITED_CLASS}`).forEach(widget => this.undecorate(widget));
        document.querySelectorAll('[data-ai-preview-placeholder], .howspace-ai-preview-container')
            .forEach(element => element.remove());
    }

    /**
     * Watches the page while something is previewed on it, and lays the preview over it again once the page has
     * settled for a frame.
     */
    private observe(): void {
        if (this.observer !== null) {
            return;
        }
        this.observer = new MutationObserver(() => {
            if (this.frame !== 0) {
                return;
            }
            this.frame = window.requestAnimationFrame(() => {
                this.frame = 0;
                this.sync();
            });
        });
        this.connect();
    }

    private connect(): void {
        this.observer?.observe(document.getElementById('main') ?? document.body, {childList: true, subtree: true});
    }

    private stopObserving(): void {
        this.observer?.disconnect();
        this.observer = null;
        if (this.frame !== 0) {
            window.cancelAnimationFrame(this.frame);
            this.frame = 0;
        }
    }

    /**
     * The preview's own changes to the page must not wake the observer, or it would redraw itself forever.
     */
    private withoutObserving(change: () => void): void {
        this.observer?.disconnect();
        try {
            change();
        } finally {
            this.connect();
        }
    }

    private label(key: string): string {
        return this.options.labels[key] || '';
    }
}
